import { useNavigate } from 'react-router-dom'
import { Order } from '../types'
import { formatMoney, formatDate, orderStatusLabel, orderStatusColor } from '../utils/format'
import { useAuth } from '../context/AuthContext'
import { useT } from '../i18n'

interface Props {
  order: Order
  adminMode?: boolean
}

export default function OrderCard({ order, adminMode }: Props) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)

  const productName = order.product
    ? (lang === 'uz' ? order.product.name_uz : order.product.name_ru)
    : `${tr('orderWord')} ${order.registry_num}`

  const dest = adminMode ? `/admin/orders/${order.id}` : `/orders/${order.id}`
  const paidCount = order.payments?.filter(p => p.status === 'paid').length ?? 0
  const totalCount = order.payments?.length ?? order.months

  return (
    <button
      onClick={() => navigate(dest)}
      className="card w-full text-left active:opacity-70 transition-opacity"
      style={{ display: 'block' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          {adminMode && order.user ? (
            <>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#F5F4F0' }}>
                {order.user.full_name}
              </div>
              <div style={{ marginTop: 2, fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#8A8A96' }}>
                {order.registry_num} · {productName}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#8A8A96' }}>
                {order.registry_num}
              </div>
              <div style={{ marginTop: 4, fontSize: 14, fontWeight: 500, color: '#F5F4F0' }}>
                {productName}
              </div>
            </>
          )}
          <div style={{ marginTop: 2, fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#3F3F45' }}>
            {formatDate(order.submitted_at)}
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${orderStatusColor(order.status)}`}>
          {orderStatusLabel(order.status, lang)}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <Stat label={tr('monthly').toUpperCase()} value={formatMoney(order.monthly_amount)} />
        <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
        <Stat label={tr('dueDate').toUpperCase()} value={`${order.months} ${tr('monthsShort')}`} />
        <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
        <Stat label={tr('paidAt').toUpperCase()} value={`${paidCount} / ${totalCount}`} />
        <div style={{ marginLeft: 'auto', color: '#8A8A96' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>
    </button>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{
        fontSize: 9, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '0.12em',
        color: '#3F3F45',
      }}>{label}</div>
      <div style={{ marginTop: 4, fontFamily: '"JetBrains Mono", monospace', fontSize: 13, color: '#F5F4F0' }}>
        {value}
      </div>
    </div>
  )
}
